import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";

import { NetworkResponse } from "~/app/type/common";
import { NOTIFICATIONS } from "~/constans/notifications";
import { useAppeals } from "~/entity/Appeals";
import {
  IAppealFileSetterParams,
  IAppealResolveParams,
  IAppealSetParams,
} from "~/entity/Appeals/model/schema";
import { useAddNotificationMutation } from "~/schemas/generated/graphql";
import { defaultErrorHandlerFunc } from "~/shared/hooks/useRequestHandler";

export const useSetAppeal = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [addNotificationMutation] = useAddNotificationMutation({});
  const {
    queryKeys,
    sentAppeal,
    needInfoAppeal,
    reviewAppeal,
    setAppeal,
    addFile,
    deleteFile,
    closeAppeal,
    setAppealPeriod,
    setAppealDecision,
  } = useAppeals();

  const _ResolveFiles = async (
    data: Omit<IAppealFileSetterParams, "fileId">,
    fileIds: string[],
    oldFileIds: string[],
    mode?: "all" | "onlyDelete",
  ) => {
    // Файлы для удаления: есть в oldFileIds, но нет в fileIds
    const filesToDelete = oldFileIds.filter(
      (fileId) => !fileIds.includes(fileId),
    );

    // Файлы для добавления: есть в fileIds, но нет в oldFileIds
    const filesToAdd = fileIds.filter((fileId) => !oldFileIds.includes(fileId));

    const deletePromises = filesToDelete.length
      ? filesToDelete.map((fileId) => deleteFile({ ...data, fileId }))
      : [];

    const addPromises =
      mode === "all"
        ? filesToAdd.length
          ? filesToAdd.map((fileId) => addFile({ ...data, fileId }))
          : []
        : [];

    if (deletePromises.length || addPromises.length) {
      return Promise.all([...deletePromises, ...addPromises]);
    }
  };

  const _saveAppealAndFiles = async (data: IAppealSetParams) => {
    const { scope, fileIds, fileIdsOld, ...appealSetterData } = data;

    const response = await setAppeal(appealSetterData);
    if (appealSetterData.id) {
      await _ResolveFiles(
        { scope, appealId: appealSetterData.id },
        fileIds || [],
        fileIdsOld || [],
        "all",
      );
      return response;
    }
  };

  const _resolveAppealAndFiles = async (data: IAppealResolveParams) => {
    const { scope, fileIds, fileIdsOld, ...appealSetterData } = data;
    const convertedAppealId = Number(data.id);

    if (appealSetterData.id) {
      await _ResolveFiles(
        { scope, appealId: appealSetterData.id },
        fileIds || [],
        fileIdsOld || [],
        "onlyDelete",
      );
      const convertedFileIds = fileIds.map((el) => Number(el));

      return await closeAppeal({
        id: convertedAppealId,
        fileIds: convertedFileIds,
        comment: appealSetterData.comment,
        status: appealSetterData.status,
      });
    }
  };

  const _cascadeSaveRequest = async (appealSetterData: IAppealSetParams) => {
    await _saveAppealAndFiles(appealSetterData);
    if (appealSetterData.id) {
      return sentAppeal(appealSetterData.id);
    }
  };

  const _needInfoAppeal = async (params: {
    id: string | number;
    comment: string;
  }): Promise<NetworkResponse<unknown>> => {
    return needInfoAppeal(params.id, params.comment);
  };

  const saveDraft = useMutation(_saveAppealAndFiles, {
    onSuccess: () => {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("appeal.notification.setSuccess"),
        },
      });
      queryClient.invalidateQueries(queryKeys.allWOFilter);
    },
    onError: (e) => {
      defaultErrorHandlerFunc({
        e,
        t,
        addNotificationMutation,
      });
    },
  });

  const saveAndSentAppeal = useMutation(_cascadeSaveRequest, {
    onSuccess: () => {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("appeal.notification.sentSuccess"),
        },
      });
      queryClient.invalidateQueries(queryKeys.allWOFilter);
    },
    onError: (e) => {
      defaultErrorHandlerFunc({
        e,
        t,
        addNotificationMutation,
      });
    },
  });

  const resolveAppeal = useMutation(_resolveAppealAndFiles, {
    onSuccess: () => {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("appeal.notification.resolveSuccess"),
        },
      });
      queryClient.invalidateQueries(queryKeys.allWOFilter);
    },
    onError: (e) => {
      defaultErrorHandlerFunc({
        e,
        t,
        addNotificationMutation,
      });
    },
  });

  const setAppealPeriodMutation = useMutation(setAppealPeriod, {
    onSuccess: () => {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("appeal.notification.periodSetSuccess", {
            defaultValue: "Период подачи апелляций установлен",
          }),
        },
      });
      queryClient.invalidateQueries(queryKeys.allWOFilter);
    },
    onError: (e) => {
      defaultErrorHandlerFunc({
        e,
        t,
        addNotificationMutation,
      });
    },
  });

  const setAppealReview = useMutation(reviewAppeal, {
    onSuccess: () => {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("appeal.notification.appealSetStatusOnReview", {
            defaultValue: "Апелляция принята к рассмотрению",
          }),
        },
      });
      queryClient.invalidateQueries(queryKeys.allWOFilter);
    },
    onError: (e) => {
      defaultErrorHandlerFunc({
        e,
        t,
        addNotificationMutation,
      });
    },
  });

  const setAppealNeedInfo = useMutation(_needInfoAppeal, {
    onSuccess: () => {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("appeal.notification.appealNeedInfoRequest", {
            defaultValue: "Запрос на дополнительную информацию отправлен",
          }),
        },
      });
      queryClient.invalidateQueries(queryKeys.allWOFilter);
    },
    onError: (e) => {
      defaultErrorHandlerFunc({
        e,
        t,
        addNotificationMutation,
      });
    },
  });

  const setAppealDecisionMutation = useMutation(setAppealDecision, {
    onSuccess: () => {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("appeal.notification.decisionChangeSaved", {
            defaultValue: "Изменение решения по апелляции сохранено",
          }),
        },
      });
      queryClient.invalidateQueries(queryKeys.allWOFilter);
    },
    onError: (e) => {
      defaultErrorHandlerFunc({
        e,
        t,
        addNotificationMutation,
      });
    },
  });

  return {
    saveDraft,
    saveAndSentAppeal,
    resolveAppeal,
    setAppealPeriod: setAppealPeriodMutation,
    setAppealNeedInfo,
    setAppealReview,
    setAppealDecisionMutation,
  };
};
